import { Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { Validators as RegExps } from "@core/constants/validators";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { PcpSelectionModalComponent } from "@core/components/pcp-selection-modal/pcp-selection-modal.component";
import { catchError, takeUntil } from "rxjs/operators";
import { of, ReplaySubject } from "rxjs";
import { PcpService } from "@core/data/pcp.service";
import { FeatureFlagsService } from "@core/services/feature-flags.service";
import { DISABLE_PCP_WIDGET_V3 } from "@core/constants/feature-flags";
import { ListPcpResponse as ListPcpResponseV3 } from "@core/interfaces/3rdparty/pcp/v3/list-pcp-response";
import { PcpItem as PcpItemV3 } from "@core/interfaces/3rdparty/pcp/v3/pcp-item";
import { ListPcpResponse as ListPcpResponseV2 } from "@core/interfaces/3rdparty/pcp/v2/list-pcp-response";
import { PcpItem as PcpItemV2 } from "@core/interfaces/3rdparty/pcp/v2/pcp-item";

@Component({
    selector: "app-pcp-search",
    templateUrl: "./pcp-search.component.html",
    styleUrls: ["./pcp-search.component.scss"],
    standalone: false,
})
export class PcpSearchComponent implements OnDestroy {
    @Input() lobCode = "HFSG";
    @Input() companyCode = 30;
    @Output() pcpSelected = new EventEmitter();
    public searchForm: UntypedFormGroup;
    public readonly alphaSpaceRegEx = { S: { pattern: RegExps.alphaSpaceRegEx } };
    private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    private modalRef: NgbModalRef;

    constructor(
        private fb: UntypedFormBuilder,
        private modalService: NgbModal,
        private pcpService: PcpService,
        private featureFlags: FeatureFlagsService,
    ) {
        this.searchForm = this.fb.group({
            zip: [null, Validators.required],
            pcpName: [null],
        });
    }

    get invalidZip() {
        const zipControl = this.searchForm.get("zip");
        return zipControl?.invalid && (zipControl.dirty || zipControl.touched || this.searchForm.touched);
    }

    get useV2(): boolean {
        return this.featureFlags.isFeatureEnabled(DISABLE_PCP_WIDGET_V3);
    }

    openPcpSelectionModal() {
        if (this.searchForm.invalid) {
            this.searchForm.markAllAsTouched();
            return;
        }

        this.modalRef = this.modalService.open(PcpSelectionModalComponent, {
            centered: true,
            size: "lg",
            scrollable: true,
            windowClass: "force-full-screen",
            backdrop: false,
        });
        this.modalRef.componentInstance.apiVersion = this.useV2 ? "v2" : "v3";

        if (this.useV2) {
            this.searchProviderV2();
        } else {
            this.searchProvider();
        }

        this.modalRef.componentInstance.pageChanged
            .pipe(takeUntil(this.destroyed$))
            .subscribe((paginationQuery: { page: number; pageSize: number }) => {
                if (this.useV2) {
                    this.searchProviderV2(paginationQuery);
                } else {
                    this.searchProvider(paginationQuery);
                }
            });

        this.modalRef.componentInstance.selectionMade.pipe(takeUntil(this.destroyed$)).subscribe((pcpInfo: PcpItemV2 | PcpItemV3) => {
            this.pcpSelected.emit(pcpInfo);
        });
    }

    searchProvider(paginationQuery: { page?: number; pageSize?: number } = {}) {
        const formValue = this.searchForm.value;
        this.pcpService
            .listPcpV3({
                ...{
                    page: 0,
                    pageSize: 10,
                    query: "",
                    acceptingNewPatients: true,
                    onlyPcps: true,
                    sorting: "location",
                },
                ...paginationQuery,
                ...{
                    zipCode: formValue.zip,
                    query: formValue.pcpName || "",
                    lob: this.lobCode,
                    company: this.companyCode,
                },
            })
            .pipe(
                takeUntil(this.destroyed$),
                catchError(() => of({ data: { totalResults: 0, results: [] } })),
            )
            .subscribe(
                (response: ListPcpResponseV3) =>
                    (this.modalRef.componentInstance.pcpProviders = {
                        total: response.data.totalResults,
                        items: response.data.results,
                    }),
            );
    }

    searchProviderV2(paginationQuery: { page?: number; pageSize?: number } = {}) {
        const formValue = this.searchForm.value;
        this.pcpService
            .listPcp({
                ...{
                    entityType: "provider",
                    page: 0,
                    pageSize: 10,
                    query: "",
                    fq: "acceptingNewPatients:true;onlyPcps:PCP",
                    sorting: "location",
                },
                ...paginationQuery,
                ...{
                    zipCode: formValue.zip,
                    query: formValue.pcpName || "",
                    lob: this.lobCode,
                    company: this.companyCode,
                },
            })
            .pipe(
                takeUntil(this.destroyed$),
                catchError(() => of({ data: { totalResults: 0, results: { providers: [] } } })),
            )
            .subscribe(
                (response: ListPcpResponseV2) =>
                    (this.modalRef.componentInstance.pcpProviders = {
                        total: response.data.totalResults,
                        items: response.data.results.providers,
                    }),
            );
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
